import React, { useRef, useState, useEffect } from 'react';
import './Roleta.scss';

// Desenha a roleta no canvas usando os prêmios recebidos
export default function RoletaCanvas({ premios, girando, anguloFinal, onAnimacaoFim }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const anguloAtual = useRef(0);

  const TAM = 420;
  const RAIO = TAM / 2 - 10;
  const CX = TAM / 2;
  const CY = TAM / 2;

  // VISUAL: todos os segmentos têm o mesmo tamanho na roleta.
  // A probabilidade real é aplicada no sorteio (server-side), não no desenho.
  // Isso evita prêmios raros virarem fatias minúsculas e dá sensação de chance pro usuário.
  function getSegmentos() {
    const angulo = (2 * Math.PI) / premios.length;
    let inicio = -Math.PI / 2; // começa no topo
    return premios.map((p) => {
      const seg = { ...p, inicio, fim: inicio + angulo };
      inicio += angulo;
      return seg;
    });
  }

  function desenhar(angulo) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const segs = getSegmentos();

    ctx.clearRect(0, 0, TAM, TAM);

    // Sombra externa
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(CX, CY, RAIO + 4, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.restore();

    segs.forEach((seg) => {
      const ini = seg.inicio + angulo;
      const fim = seg.fim + angulo;

      // Fatia
      ctx.beginPath();
      ctx.moveTo(CX, CY);
      ctx.arc(CX, CY, RAIO, ini, fim);
      ctx.closePath();
      ctx.fillStyle = seg.cor;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Texto
      const meio = (ini + fim) / 2;
      const tx = CX + (RAIO * 0.62) * Math.cos(meio);
      const ty = CY + (RAIO * 0.62) * Math.sin(meio);

      ctx.save();
      ctx.translate(tx, ty);
      ctx.rotate(meio + Math.PI / 2);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 4;

      const linhas = quebrarTexto(seg.nome, 14);
      linhas.forEach((linha, i) => {
        ctx.fillText(linha, 0, (i - (linhas.length - 1) / 2) * 14);
      });
      ctx.restore();
    });

    // Centro
    ctx.beginPath();
    ctx.arc(CX, CY, 22, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(CX, CY, 14, 0, 2 * Math.PI);
    ctx.fillStyle = '#feac24';
    ctx.fill();
  }

  function quebrarTexto(texto, maxChar) {
    const palavras = texto.split(' ');
    const linhas = [];
    let atual = '';
    for (const p of palavras) {
      if ((atual + ' ' + p).trim().length > maxChar) {
        if (atual) linhas.push(atual.trim());
        atual = p;
      } else {
        atual = (atual + ' ' + p).trim();
      }
    }
    if (atual) linhas.push(atual.trim());
    return linhas;
  }

  // Renderização estática
  useEffect(() => {
    if (!girando) {
      desenhar(anguloAtual.current);
    }
  }, [premios]);

  // Animação de giro
  useEffect(() => {
    if (!girando || anguloFinal === null) return;

    const duracao = 5000; // ms
    const inicio = performance.now();
    const anguloInicio = anguloAtual.current;
    const TWO_PI = 2 * Math.PI;
    // Pra setinha alinhar com o segmento vencedor, o ângulo final (módulo 2π)
    // precisa ser EXATAMENTE anguloFinal. Calcula o delta necessário a partir
    // da posição atual (mantém o giro sempre indo pra frente).
    const deltaParaCair = ((anguloFinal - anguloInicio) % TWO_PI + TWO_PI) % TWO_PI;
    const alvo = anguloInicio + 5 * TWO_PI + deltaParaCair;

    function animar(agora) {
      const t = Math.min((agora - inicio) / duracao, 1);
      const ease = easeOutCubic(t);
      anguloAtual.current = anguloInicio + (alvo - anguloInicio) * ease;
      desenhar(anguloAtual.current);

      if (t < 1) {
        animRef.current = requestAnimationFrame(animar);
      } else {
        // Trava no ângulo final exato (não em alvo % 2π, que já é igual mas evita float drift)
        anguloAtual.current = anguloFinal;
        desenhar(anguloAtual.current);
        onAnimacaoFim && onAnimacaoFim();
      }
    }

    animRef.current = requestAnimationFrame(animar);
    return () => cancelAnimationFrame(animRef.current);
  }, [girando, anguloFinal]);

  return (
    <div className="roleta-wrapper">
      {/* Seta indicadora */}
      <div className="roleta-seta" />
      <canvas ref={canvasRef} width={TAM} height={TAM} className="roleta-canvas" />
    </div>
  );
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}
